if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.refresh();
      }

      refresh() {
        this.elements = {
          dotPagination: this.querySelector('[data-product-gallery-dot]')?.parentElement,
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer && this.elements.viewer.dataset.mediaGalleryBound !== 'true') {
          this.elements.viewer.dataset.mediaGalleryBound = 'true';
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        this.updateDotPagination(1);

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button || button.dataset.mediaGalleryBound === 'true') return;
          button.dataset.mediaGalleryBound = 'true';
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        this.updateDotPagination(event.detail.currentPage);

        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      updateDotPagination(currentPage = 1) {
        const dots = Array.from(this.querySelectorAll('[data-product-gallery-dot]'));

        if (!dots.length) return;

        const total = dots.length;
        const page = Math.min(Math.max(Number(currentPage) || 1, 1), total);
        const visibleCount = Math.min(5, total);
        let firstVisible = Math.max(1, page - Math.floor(visibleCount / 2));
        let lastVisible = firstVisible + visibleCount - 1;

        if (lastVisible > total) {
          lastVisible = total;
          firstVisible = Math.max(1, lastVisible - visibleCount + 1);
        }

        dots.forEach((dot, index) => {
          const dotPosition = index + 1;
          dot.classList.toggle('is-active', dotPosition === page);
          dot.classList.toggle('is-hidden', dotPosition < firstVisible || dotPosition > lastVisible);
        });
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements?.viewer) return;

        let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);

        if (!activeMedia && mediaId) {
          const rawMediaId = String(mediaId).split('-').pop();
          activeMedia = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).find((element) =>
            element.dataset.mediaId?.endsWith(`-${rawMediaId}`)
          );
        }

        if (!activeMedia && !mediaId) activeMedia = this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) {
          return;
        }
        const activeMediaId = activeMedia.dataset.mediaId;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMediaId}"]`);
            activeThumbnail &&
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.updateDotPagination(Number(activeThumbnail?.dataset.mediaPosition) || 1);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
